import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import AddAthleteProfileForm, { AthleteProfile } from "./AddAthleteProfileForm";
import { buildApiUrl } from "../utils/apiConfig";
import { useSubscriptionLimits } from "../hooks/useSubscriptionLimits";
import SubscriptionLimitModal from "./SubscriptionLimitModal";

type LocationState =
    | { athlete?: Partial<AthleteProfile> & { id?: string | number } }
    | undefined;

export default function AddAthleteProfilePage() {
    const { id } = useParams<{ id?: string }>(); // /athletes/:id/edit or /athletes/new
    const navigate = useNavigate();
    const state = useLocation().state as LocationState;
    const { limits, refetch: refetchLimits } = useSubscriptionLimits();
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitsChecked, setLimitsChecked] = useState(false);

    // Check subscription limits when creating a new athlete (not editing)
    useEffect(() => {
        const checkLimits = async () => {
            // Only check for new athletes, not edits
            if (id) {
                setLimitsChecked(true);
                return;
            }

            try {
                // Fetch fresh limits to ensure accuracy
                const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
                const freshLimits = await fetchSubscriptionLimits();

                // Check if at limit
                if (isAtAthleteLimit(freshLimits)) {
                    setShowLimitModal(true);
                }

                // Also refetch the hook state for UI updates
                await refetchLimits();
            } catch (error) {
                // If check fails, show limit modal to be safe
                setShowLimitModal(true);
            } finally {
                setLimitsChecked(true);
            }
        };

        checkLimits();
    }, [id, refetchLimits]);

    const handleModalClose = () => {
        setShowLimitModal(false);
        navigate(-1); // Go back when modal is closed
    };

    const close = () => navigate(-1); // go back to the previous page (team)

    // View endpoint that returns dropdown data (states, staff …)
    const userId = localStorage.getItem("userId") || "";
    const loadUrl = userId
        ? buildApiUrl(`api/athletes/add/view?userId=${encodeURIComponent(userId)}`)
        : buildApiUrl("api/athletes/add/view");

    // Pass base URL for creates - the form will construct the correct PUT URL for updates internally
    const saveUrl = userId
        ? buildApiUrl(`api/athletes/${encodeURIComponent(userId)}`)
        : buildApiUrl("api/athletes");

    // Show loading while checking limits for new athletes
    if (!id && !limitsChecked) {
        return (
            <section className="add-member-profile">
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div className="spinner" />
                    <p>Checking subscription limits...</p>
                </div>
            </section>
        );
    }

    return (
        <section className="add-member-profile">
            {!id && showLimitModal ? (
                <SubscriptionLimitModal
                    open={showLimitModal}
                    onClose={handleModalClose}
                    limits={limits}
                />
            ) : (
                <AddAthleteProfileForm
                    loadUrl={loadUrl}
                    saveUrl={saveUrl}
                    useCookies
                    // If we navigated here from a card, `state.athlete` pre-fills the form.
                    initial={state?.athlete}
                    afterSaveTo={"/team"}
                    onClose={close}
                />
            )}
        </section>
    );
}